import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createProductList,
  getProductListItemsAsPlanItems,
  getProductListItems,
  getProductList,
  getProductListErrorReport,
  type ProcessingResponse,
  type ProductListRead,
  type ProductListItemRead,
  type PaginatedProductListItems,
} from '@/api/productLists'
import type { PlanItem } from '@/api/planItems'

export interface CreateProductListParams {
  fileId: string
  name: string
  description?: string
}

/**
 * Mutation hook for creating a product list from an uploaded file
 * Returns processing info with SSE URL for status updates
 */
export function useCreateProductList() {
  return useMutation<ProcessingResponse, Error, CreateProductListParams>({
    mutationFn: createProductList,
  })
}

/**
 * Query hook for fetching a product list by ID
 */
export function useProductList(productListId: string | null) {
  return useQuery<ProductListRead, Error>({
    queryKey: ['productList', productListId],
    queryFn: () => getProductList(productListId!),
    enabled: !!productListId,
  })
}

/**
 * Query hook for fetching product list items with pagination
 * Maps items to PlanItem format for display in the wizard
 */
export function useProductListItems(
  productListId: string | null,
  page: number = 1,
  pageSize: number = 5
) {
  return useQuery<
    {
      items: PlanItem[]
      totalCount: number
      page: number
      pageSize: number
      totalPages: number
    },
    Error
  >({
    queryKey: ['productListItems', productListId, page, pageSize],
    queryFn: () =>
      getProductListItemsAsPlanItems(productListId!, page, pageSize),
    enabled: !!productListId,
    staleTime: 0, // Always fetch fresh data
  })
}

/**
 * Query hook for fetching rejected items (is_valid=false)
 */
export function useProductListRejectedItems(
  productListId: string | null,
  page: number = 1,
  pageSize: number = 5,
  enabled: boolean = true
) {
  return useQuery<PaginatedProductListItems, Error>({
    queryKey: ['productListRejectedItems', productListId, page, pageSize],
    queryFn: () => getProductListItems(productListId!, page, pageSize, false),
    enabled: !!productListId && enabled,
    staleTime: 0,
  })
}

/**
 * Fetches a batch of valid items and filters client-side for those with warnings.
 * No pagination — the backend doesn't support filtering by has_warnings,
 * so we fetch a large batch and return all warnings found.
 * Users can download the CSV error report for the complete list.
 */
export function useProductListWarningItems(
  productListId: string | null,
  enabled: boolean = true
) {
  return useQuery<ProductListItemRead[], Error>({
    queryKey: ['productListWarningItems', productListId],
    queryFn: async () => {
      const response = await getProductListItems(productListId!, 1, 200, true)
      return response.data.filter(item => item.errors && item.errors.length > 0)
    },
    enabled: !!productListId && enabled,
    staleTime: 0,
  })
}

/**
 * Mutation hook for downloading the error report CSV
 */
export function useProductListErrorReport() {
  return useMutation<{ download_url: string }, Error, string>({
    mutationFn: getProductListErrorReport,
  })
}
